/**
 * Échecs de chargement d'un écran chargé à la demande (cf. `src/index.tsx`).
 *
 * Le cas visé est le déploiement : un onglet ouvert sur l'ancienne version demande, à la première
 * ouverture d'un écran, un chunk que la nouvelle image ne contient plus. nginx ne répond pas 404
 * mais sert `index.html` (`try_files` de `nginx.conf`), que le navigateur refuse comme module. Seul
 * un rechargement de la page récupère la nouvelle version.
 *
 * Vite signale ces échecs par l'événement `vite:preloadError`, émis par son assistant de
 * préchargement autour de chaque import dynamique, puis relance l'erreur si l'événement n'est pas
 * annulé. On le laisse la relancer : le routeur termine alors la navigation sur l'URL visée et y
 * affiche son `errorElement`, qui peut recharger la page au bon endroit. L'écouteur se contente de
 * marquer l'erreur, ce qui évite de reconnaître un échec de chargement à son message, propre à
 * chaque navigateur.
 */

const chunkLoadErrors = new WeakSet<object>();

/** Marque comme échecs de chargement les erreurs portées par `vite:preloadError`. */
export const listenToChunkLoadErrors = (target: EventTarget) => {
  target.addEventListener("vite:preloadError", (event) => {
    const error = (event as Event & {payload?: unknown}).payload;
    if (typeof error === "object" && error !== null) {
      chunkLoadErrors.add(error);
    }
  });
};

export const isChunkLoadError = (error: unknown) =>
  typeof error === "object" && error !== null && chunkLoadErrors.has(error);

export const RELOAD_STORAGE_KEY = "chunkLoadErrorReloadedAt";

/**
 * Délai en deçà duquel un second échec n'entraîne pas de nouveau rechargement : si le chunk manque
 * encore juste après, le recharger en boucle n'y changerait rien, et l'utilisateur doit voir
 * l'erreur.
 */
export const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Indique s'il faut recharger la page, et note l'heure du rechargement le cas échéant. La note vit
 * dans le `sessionStorage` pour survivre au rechargement lui-même.
 */
export const claimChunkReload = (storage: Pick<Storage, "getItem" | "setItem">, now: number) => {
  const reloadedAt = Number(storage.getItem(RELOAD_STORAGE_KEY));
  if (Number.isFinite(reloadedAt) && now - reloadedAt < RELOAD_COOLDOWN_MS) {
    return false;
  }
  storage.setItem(RELOAD_STORAGE_KEY, String(now));
  return true;
};
